import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Group, Modal, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { getSubject, updateSubject } from '../../api/resources';
import { EntityRef } from '../EntityRef';
import { CopyableId } from '../CopyableText';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import type { Organization } from '../../types/api';
import { DetailField, DetailFieldGrid } from './DetailField';
import { DetailSection } from './DetailSection';
import { KeyFactsSummary } from './facts/KeyFactsSummary';
import { SubjectFactsSection } from './SubjectFactsSection';

export function SubjectDetailPanel({ subjectId }: { subjectId: string }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const lookups = useAdminLookups();
    const [editOpen, setEditOpen] = useState(false);
    const [fullName, setFullName] = useState('');
    const [primaryPhone, setPrimaryPhone] = useState('');
    const [alternativePhone, setAlternativePhone] = useState('');
    const [claimedBusinessName, setClaimedBusinessName] = useState('');

    const subjectQ = useQuery({
        queryKey: ['subject', subjectId],
        queryFn: () => getSubject(subjectId),
    });

    const subject = subjectQ.data;
    const caseLinks = subject?.caseSubjects ?? [];
    const poiLinks = subject?.poiSubjects ?? [];

    const updateMutation = useMutation({
        mutationFn: () =>
            updateSubject(subjectId, {
                fullName: fullName.trim() || undefined,
                primaryPhone: primaryPhone.trim() || undefined,
                alternativePhone: alternativePhone.trim() || undefined,
                claimedBusinessName: claimedBusinessName.trim() || undefined,
            }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.subjects.updated') });
            setEditOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['subject', subjectId] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const relatedOrganizations = useMemo(() => {
        const byId = new Map<string, Organization>();
        for (const link of caseLinks) {
            const org = link.caseRecord?.organization;
            const orgId = link.caseRecord?.organizationId;
            if (orgId && org) {
                byId.set(orgId, org);
            } else if (orgId) {
                const resolved = lookups.organizations.get(orgId);
                if (resolved) {
                    byId.set(orgId, resolved);
                }
            }
        }
        return [...byId.values()];
    }, [caseLinks, lookups.organizations]);

    if (!subject) {
        return <Text c="dimmed">{t('common.loading.subject')}</Text>;
    }

    const empty = t('common.empty');

    const openEdit = () => {
        setFullName(subject.fullName ?? '');
        setPrimaryPhone(subject.primaryPhone ?? '');
        setAlternativePhone(subject.alternativePhone ?? '');
        setClaimedBusinessName(subject.claimedBusinessName ?? '');
        setEditOpen(true);
    };

    return (
        <>
            <Group justify="flex-end" mb="md">
                <Button size="xs" variant="light" onClick={openEdit}>
                    {t('common.actions.edit')}
                </Button>
            </Group>
            <DetailFieldGrid>
                <DetailField label={t('fields.fullName')} value={subject.fullName ?? empty} />
                <DetailField
                    label={t('fields.type')}
                    value={
                        subject.subjectType ? (
                            <Badge variant="light">
                                {t(`subjectTypes.${subject.subjectType}`, {
                                    defaultValue: subject.subjectType,
                                })}
                            </Badge>
                        ) : (
                            empty
                        )
                    }
                />
                <DetailField label={t('fields.primaryPhone')} value={subject.primaryPhone ?? empty} />
                <DetailField
                    label={t('fields.alternativePhone')}
                    value={subject.alternativePhone ?? empty}
                />
                <DetailField
                    label={t('fields.businessName')}
                    value={subject.claimedBusinessName ?? empty}
                />
                <DetailField label={t('fields.subjectId')} value={<CopyableId id={subject.id} />} />
                <DetailField
                    label={t('fields.lastVerified')}
                    value={
                        subject.lastVerifiedAt ?
                            new Date(subject.lastVerifiedAt).toLocaleString()
                        :   empty
                    }
                />
                <DetailField
                    label={t('fields.created')}
                    value={subject.createdAt ? new Date(subject.createdAt).toLocaleString() : empty}
                />
            </DetailFieldGrid>

            <KeyFactsSummary entityKind="subject" facts={subject.facts ?? []} />

            <DetailSection
                title={t('detail.subjectOrganizations')}
                description={t('detail.subjectOrganizationsHint')}
            >
                {relatedOrganizations.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noSubjectOrganizations')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.organization')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {relatedOrganizations.map((org) => (
                                <Table.Tr key={org.id}>
                                    <Table.Td>
                                        <EntityRef
                                            title={org.name}
                                            id={org.id}
                                            href={`/organizations?id=${org.id}`}
                                            hideId
                                        />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <DetailSection
                title={t('detail.subjectCases')}
                description={t('detail.subjectCasesHint')}
            >
                {caseLinks.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noSubjectCases')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.case')}</Table.Th>
                                <Table.Th>{t('fields.organization')}</Table.Th>
                                <Table.Th>{t('fields.status')}</Table.Th>
                                <Table.Th>{t('fields.roleOnCase')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {caseLinks.map((link) => {
                                const caseRow = link.caseRecord;
                                const org = lookups.resolveOrganization(
                                    caseRow?.organizationId,
                                    caseRow?.organization,
                                );
                                return (
                                    <Table.Tr key={link.id}>
                                        <Table.Td>
                                            <EntityRef
                                                title={
                                                    caseRow?.caseType
                                                        ? t(`caseTypes.${caseRow.caseType}`, {
                                                              defaultValue: caseRow.caseType,
                                                          })
                                                        : t('entities.case')
                                                }
                                                id={link.caseId}
                                                href={`/cases/${link.caseId}`}
                                                hideId
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            {caseRow?.organizationId ? (
                                                <EntityRef
                                                    title={org.title}
                                                    id={org.id}
                                                    href={`/organizations?id=${caseRow.organizationId}`}
                                                    hideId
                                                />
                                            ) : (
                                                empty
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            {caseRow?.status ? (
                                                <Badge variant="light" size="sm">
                                                    {t(`caseStatuses.${caseRow.status}`, {
                                                        defaultValue: caseRow.status,
                                                    })}
                                                </Badge>
                                            ) : (
                                                empty
                                            )}
                                        </Table.Td>
                                        <Table.Td>{link.role ?? empty}</Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <DetailSection
                title={t('detail.subjectPois')}
                description={t('detail.subjectPoisHint')}
            >
                {poiLinks.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noSubjectPois')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.poi')}</Table.Th>
                                <Table.Th>{t('fields.relationship')}</Table.Th>
                                <Table.Th>{t('fields.current')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {poiLinks.map((link) => (
                                <Table.Tr key={link.id}>
                                    <Table.Td>
                                        <EntityRef
                                            title={link.poi?.name ?? t('entities.poi')}
                                            id={link.poiId}
                                            secondary={link.poi?.claimedAddress}
                                            href={`/pois?id=${link.poiId}`}
                                            hideId
                                        />
                                    </Table.Td>
                                    <Table.Td>{link.relationshipType ?? empty}</Table.Td>
                                    <Table.Td>
                                        {link.isCurrent ? t('common.yes') : t('common.no')}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <DetailSection
                title={t('detail.subjectMedia')}
                description={t('detail.subjectMediaHint')}
            >
                {(() => {
                    const selfies = (subject.media ?? []).filter((m) => m.category === 'selfie');
                    if (selfies.length === 0) {
                        return (
                            <Text size="sm" c="dimmed">
                                {t('detail.noSubjectMedia')}
                            </Text>
                        );
                    }
                    return (
                        <Stack gap="sm">
                            {selfies.map((item) => {
                                const href = item.downloadUrl ?? item.originalUrl ?? undefined;
                                return (
                                    <Group key={item.id} gap="sm" align="flex-start">
                                        {href ? (
                                            <Text
                                                component="a"
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                size="sm"
                                            >
                                                {t('detail.viewSelfie')}
                                            </Text>
                                        ) : (
                                            <Text size="sm" c="dimmed">
                                                {t('detail.selfieUnavailable')}
                                            </Text>
                                        )}
                                        <Badge variant="light" size="sm">
                                            {item.mediaType}
                                        </Badge>
                                    </Group>
                                );
                            })}
                        </Stack>
                    );
                })()}
            </DetailSection>

            <SubjectFactsSection facts={subject.facts ?? []} />

            <Modal opened={editOpen} onClose={() => setEditOpen(false)} title={t('features.subjects.editTitle')}>
                <Stack gap="md">
                    <TextInput label={t('fields.fullName')} value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
                    <TextInput label={t('fields.primaryPhone')} value={primaryPhone} onChange={(e) => setPrimaryPhone(e.currentTarget.value)} />
                    <TextInput label={t('fields.alternativePhone')} value={alternativePhone} onChange={(e) => setAlternativePhone(e.currentTarget.value)} />
                    <TextInput label={t('fields.businessName')} value={claimedBusinessName} onChange={(e) => setClaimedBusinessName(e.currentTarget.value)} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditOpen(false)}>{t('common.actions.cancel')}</Button>
                        <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>{t('common.actions.save')}</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
